/**
 * L'escalier de déverrouillage — **avenant A-5, proposition 5** (tâche M2.37), **piloté par
 * le compteur de publications depuis l'avenant A-9**.
 *
 * « Le sentiment de progression ne vient pas de chiffres flattés : il vient de ce que le jeu
 * s'ouvre. » Au départ le jeu tient en **deux boutons** — publier un short, dormir — et les
 * mécaniques arrivent une à une pour qu'à 1 000 abonnés elles soient **toutes** là.
 *
 * ## Pourquoi les publications, et pas les abonnés ⟦A-9⟧
 *
 * Mesuré, pas supposé : conditionner un étage aux **abonnés** referme une boucle sur
 * elle-même. Le plafond de compétence écrase la qualité ⇒ moins d'abonnés ⇒ ni nœuds ni
 * paliers ⇒ le plafond ne monte jamais, l'étage ne s'ouvre jamais. La campagne de mesure
 * l'a établi : **1 nœud acheté sur 39 en 700 actions, 100 abonnés jamais atteints.**
 *
 * Le compteur de publications est **la seule chose que le débutant contrôle**. L'escalier
 * pilote donc le rythme au lieu de le suivre : le nombre de vidéos du début est fixé par le
 * **programme** (le nombre de choses à montrer), et la courbe d'abonnés est calibrée pour
 * arriver à 100 quand le programme se termine.
 *
 * ## Ce que ce module gate, et comment
 *
 * | Étage | Seuil | Qui le fait respecter |
 * |---|---|---|
 * | 1 · veille | 1 publication | `validateAction` → `mecanique_verrouillee` |
 * | 2 · lire les commentaires | 3 | `validateAction` → `mecanique_verrouillee` |
 * | 3 · image de chaîne | 4 | **affichage** — il n'y a pas d'action à refuser |
 * | 4 · les 3 tendances | 5 | **affichage** |
 * | 5 · l'arbre et les PC | 6 | `validateAction` (`acheter_skill`) → `mecanique_verrouillee` |
 * | 6 · la longue | 10 | `validateAction` (`produce_video` longue) → `format_locked` |
 * | 7 · l'argent | ⚠️ voir ci-dessous | `flags.grand_saut_done` ⟦A-1⟧ |
 *
 * ⚠️ **L'étage `argent` est le seul qui ne passe PAS au compteur de publications.** Son
 * ouverture EST « Le Grand Saut » de l'avenant **A-1** : un événement scripté attaché au
 * **palier 1 000 abonnés**, qui verse `CAPITAL_DEPART` et allume les charges. Le décrocher du
 * palier viderait A-1 de son sens (la boîte à Clicks apparaîtrait vide, sans charges et sans
 * capital), et A-9 lui-même ancre son **arc 3** à « 1 000 — l'entreprise ». La contradiction
 * apparente entre A-1 et A-9 a été **levée par ⟦A-11 §4⟧** : *« L'étage argent, c'est à
 * 1 000 abonnés, c'est sûr. »* Le creux qui précède fait partie du rythme voulu.
 *
 * ⟦A-29⟧ **Cet étage est la porte de TOUT ce qui coûte des Clicks**, et pas seulement de la
 * boutique : *« Ce qui coûte de l'argent est dans le marché. »* `validateAction` refuse en
 * `mecanique_verrouillee` toute action dont le coût comporte des Clicks tant que
 * {@link argentOuvert} est faux — une règle générale posée une fois, plutôt qu'un garde par
 * action qu'on oublierait à la prochaine.
 *
 * Les seuils sont **des valeurs de départ** ✎, ajustables au playtest sans repasser par un
 * avenant (A-5, en-tête) : ils sont donc tous ici, en clair, et nulle part ailleurs — le
 * miroir client (`client/src/game/escalier.ts`) les lit, il ne les recopie pas.
 */
/**
 * Le programme de l'arc 1, en publications ✎ — « on ne coupe rien, on étale » (A-9).
 *
 * L'ordre est celui des gestes à montrer : publier, puis regarder dehors, puis lire ce qu'on
 * te répond, puis voir la ligne que ça dessine, puis ce qui marche ailleurs, puis où mettre
 * ses points. Les **2-3 vidéos seules** de la fin de l'arc tombent entre l'arbre (6) et la
 * longue (10) : c'est là que le palier 100 doit tomber.
 */
export const PUBLICATIONS_ESCALIER = {
    veille: 1,
    lire_commentaires: 3,
    image: 4,
    tendances: 5,
    arbre: 6,
    longue: 10,
};
/** Publications requises pour ouvrir la veille (étage 1 : « après la 1re publication »). */
export const PUBLICATIONS_VEILLE = PUBLICATIONS_ESCALIER.veille;
/** Publications requises pour lire les commentaires (étage 2 : « à la 3e publication »). */
export const PUBLICATIONS_COMMENTAIRES = PUBLICATIONS_ESCALIER.lire_commentaires;
/** Publications requises pour que l'image de chaîne se mette à compter (étage 3). */
export const PUBLICATIONS_IMAGE = PUBLICATIONS_ESCALIER.image;
/** Publications requises pour voir tourner les 3 tendances (étage 4). */
export const PUBLICATIONS_TENDANCES = PUBLICATIONS_ESCALIER.tendances;
/** Publications requises pour ouvrir l'arbre et dépenser ses PC (étage 5). */
export const PUBLICATIONS_ARBRE = PUBLICATIONS_ESCALIER.arbre;
/** Publications requises pour passer à la vidéo longue et à la bibliothèque (étage 6). */
export const PUBLICATIONS_LONGUE = PUBLICATIONS_ESCALIER.longue;
/**
 * La mécanique est-elle ouverte dans cet état ? Pure, sans effet de bord — c'est la seule
 * source de vérité des seuils, que l'UI lit elle aussi pour décider quoi afficher.
 */
export function mecaniqueOuverte(state, mecanique) {
    return state.counters.videos_published >= PUBLICATIONS_ESCALIER[mecanique];
}
/**
 * ⟦A-5 / A-9⟧ **L'escalier est-il monté jusqu'en haut ?** — la source du drapeau de contenu
 * `tuto_termine`.
 *
 * ── Pourquoi ceci est un calcul et non un booléen stocké ──────────────────────
 *
 * `tuto_termine` lisait `state.flags.tutorial_done`, **que personne n'écrivait dans tout le
 * dépôt** : le champ était déclaré, initialisé à `false`, lu en condition — et jamais posé. Le
 * drapeau valait donc `false` pour toujours, et le premier événement à s'en servir serait mort
 * dans l'œuf, sans erreur ni test rouge. Trois sessions l'ont signalé, aucune ne l'a corrigé,
 * parce que la question « qui devrait l'écrire, et quand ? » n'avait pas de réponse évidente.
 *
 * Elle n'en avait pas parce que la question était mal posée. **L'escalier est entièrement
 * dérivé de `videos_published`** — aucun de ses six étages n'est stocké. `tuto_termine` était
 * le seul des quatre drapeaux dérivés à lire un stockage au lieu de se calculer, et c'est
 * exactement d'où venait le trou : un état qu'il faut penser à écrire finit par ne pas l'être.
 *
 * Le dernier étage ouvert vaut fin du tutoriel : au-delà, plus aucune mécanique ne se
 * déverrouille et le joueur a vu tous les gestes. Le maximum est lu sur la table plutôt que
 * recopié — ajouter un étage déplacera la fin toute seule.
 */
export function escalierTermine(state) {
    const dernier = Math.max(...Object.values(PUBLICATIONS_ESCALIER));
    return state.counters.videos_published >= dernier;
}
/**
 * ⟦A-29⟧ **L'étage 7 — l'argent — est-il ouvert ?** Il ne se lit pas au compteur de
 * publications mais au drapeau du Grand Saut ⟦A-1⟧, d'où sa fonction à part.
 *
 * C'est **la** source de vérité de « le joueur peut-il dépenser des Clicks », lue par
 * `validateAction` (le refus) comme par le client (ce qu'il affiche). Personne ne relit
 * `flags.grand_saut_done` à la main pour poser cette question : le jour où la porte change,
 * elle change ici.
 */
export function argentOuvert(state) {
    return state.flags.grand_saut_done;
}
/** Publications encore nécessaires pour ouvrir la mécanique (0 si elle l'est déjà). */
export function publicationsManquantes(state, mecanique) {
    return Math.max(0, PUBLICATIONS_ESCALIER[mecanique] - state.counters.videos_published);
}
//# sourceMappingURL=escalier.js.map